import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import EIT from '../assets/images/EITlogo.png';
import { motion, AnimatePresence } from 'framer-motion';

const navLinks = [
  { to: '/',                label: 'Home' },
  { to: '/about',           label: 'About' },
  { to: '/services',        label: 'Services' },
  { to: '/careers',         label: 'Careers' },
  { to: '/news-and-events', label: 'News & Events' },
];

const Navbar = () => {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const mobileMenuRef = useRef(null);

  const closeMobileMenu = useCallback(() => setIsMobileMenuOpen(false), []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (isMobileMenuOpen && mobileMenuRef.current && !mobileMenuRef.current.contains(e.target)) {
        closeMobileMenu();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobileMenuOpen, closeMobileMenu]);

  useEffect(() => {
    closeMobileMenu();
  }, [location.pathname, closeMobileMenu]);

  return (
    <motion.nav
      className="fixed top-0 left-0 w-full z-50 bg-white/80 backdrop-blur-xl border-b border-gray-100"
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5, type: 'tween' }}
    >
      <div className="max-w-7xl mx-auto w-full flex items-center justify-between px-5 lg:px-20 h-16">

        {/* Logo */}
        <Link to="/" className="flex items-center gap-2.5 flex-shrink-0">
          <img src={EIT} className="h-8" alt="EIT Logo" />
          <span className="font-bold text-blueblack hidden md:block">
            EIT Ghana Limited
          </span>
        </Link>

        {/* Pill nav – desktop */}
        <ul className="hidden md:flex items-center bg-gray-100 rounded-full px-2 py-1.5 gap-0.5">
          {navLinks.map(({ to, label }) => (
            <li key={to}>
              <Link
                to={to}
                className={`block px-4 py-1.5 text-sm font-medium rounded-full transition-all duration-200 ${
                  location.pathname === to
                    ? 'bg-white text-blueblack shadow-sm'
                    : 'text-gray-500 hover:text-blueblack'
                }`}
              >
                {label}
              </Link>
            </li>
          ))}
        </ul>

        {/* Contact CTA */}
        <Link
          to="/contact"
          className={`hidden md:block px-5 py-2 text-sm font-semibold rounded-full transition-all duration-200 flex-shrink-0 ${
            location.pathname === '/contact'
              ? 'bg-seablue text-white'
              : 'bg-blueblack text-white hover:bg-seablue'
          }`}
        >
          Contact Us
        </Link>

        {/* Mobile toggle */}
        <button
          className="md:hidden p-2 text-blueblack rounded-xl hover:bg-gray-100 transition-colors"
          onClick={() => setIsMobileMenuOpen(prev => !prev)}
          aria-expanded={isMobileMenuOpen}
          aria-label="Toggle navigation menu"
        >
          {isMobileMenuOpen ? (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          ) : (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16m-7 6h7" />
            </svg>
          )}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            ref={mobileMenuRef}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.2 }}
            className="md:hidden bg-white border-t border-gray-100 overflow-hidden shadow-lg"
          >
            <ul className="max-w-7xl mx-auto flex flex-col gap-1 py-3 px-5">
              {[...navLinks, { to: '/contact', label: 'Contact Us' }].map(({ to, label }) => (
                <li key={to}>
                  <Link
                    to={to}
                    onClick={closeMobileMenu}
                    className={`block px-4 py-2.5 text-sm font-medium rounded-xl transition-colors ${
                      location.pathname === to
                        ? 'bg-blueblack text-white'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-blueblack'
                    }`}
                  >
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.nav>
  );
};

export default Navbar;
